import React, { useState, useEffect } from "react";
import { MainLayout } from "@/components/layout/MainLayout";
import { motion } from "framer-motion";
import { ArrowRight, Plus, Minus, Save, Check, Pencil, Grid3X3, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";

import { IconPicker, renderIconByName } from "@/components/communication/IconPicker";
import { getColorClasses } from "@/components/communication/ColorPicker";
import {
  CommunicationBoardData,
  BoardCell,
  loadBoards,
  saveBoard,
  getDefaultBoard,
} from "@/lib/communicationBoardTypes";
import { useTranslation } from "@/hooks/useTranslation";

const boardIcons = ["MessageCircle", "MessageSquare", "Heart", "Home", "Star", "Zap", "Music", "Book", "Utensils", "Sun"];
const boardEmojis = ["📋", "💬", "🏠", "❤️", "🎮", "📚", "🍽️", "🎵", "⭐", "🌈"];

export default function BoardBuilder() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editId = searchParams.get("edit");
  const { t, dir } = useTranslation();

  const [board, setBoard] = useState<CommunicationBoardData>(() => {
    if (editId) {
      const boards = loadBoards();
      const existing = boards.find((b) => b.id === editId);
      if (existing) return existing;
    }
    return {
      id: `board-${Date.now()}`,
      name: t("newBoard"),
      icon: "MessageCircle",
      rows: 2,
      columns: 3,
      cells: Array(2).fill(null).map(() => Array(3).fill(null)),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
  });



  const updateGridSize = (newRows: number, newCols: number) => {
    const newCells: BoardCell[][] = Array(newRows)
      .fill(null)
      .map((_, rowIndex) =>
        Array(newCols)
          .fill(null)
          .map((_, colIndex) => board.cells[rowIndex]?.[colIndex] || null)
      );
    setBoard((prev) => ({
      ...prev,
      rows: newRows,
      columns: newCols,
      cells: newCells,
    }));
  };

  const handleCellClick = (row: number, col: number) => {
    // Navigate to cell edit page with board context
    navigate(`/communication/cell-edit?boardId=${board.id}&row=${row}&col=${col}`);
  };

  const handleSave = () => {
    saveBoard(board);
    toast.success(editId ? t("boardUpdated") : t("boardSaved"));
    navigate("/communication");
  };

  const renderCellPreview = (cell: BoardCell) => {
    if (!cell) {
      return <Plus className="w-8 h-8 text-muted-foreground/50" />;
    }

    const colorClasses = getColorClasses(cell.color);
    const displayIcon = cell.icon ? renderIconByName(cell.icon, cn("w-8 h-8", colorClasses.text)) : null;
    const displayEmoji = cell.emoji;
    const displayText = cell.type === "category" ? cell.name : cell.text;

    return (
      <div className={cn("flex flex-col items-center gap-2 w-full", colorClasses.text)}>
        {displayIcon || (displayEmoji && <span className="text-3xl">{displayEmoji}</span>)}
        <span className="text-sm font-medium text-center line-clamp-2">{displayText}</span>
        {cell.type === "category" && cell.utterances.length > 0 && (
          <span className="text-xs text-muted-foreground">{cell.utterances.length} {t("utterances")}</span>
        )}
      </div>
    );
  };

  return (
    <MainLayout>
      <div className="flex-1 flex flex-col overflow-hidden" dir={dir}>
        {/* Header */}
        <header className="flex items-center justify-between px-6 py-4 border-b border-border bg-card/50">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate("/communication")} data-scan-item className="rounded-xl">
              <ArrowRight className="w-6 h-6" />
            </Button>
            <button
              onClick={() => navigate(`/communication/builder/details?id=${board.id}`)}
              data-scan-item
              className="flex items-center gap-2 hover:bg-accent/50 px-3 py-2 rounded-xl transition-colors"
            >
              {board.icon && renderIconByName(board.icon, "w-6 h-6 text-primary")}
              {board.emoji && <span className="text-2xl">{board.emoji}</span>}
              <h1 className="text-2xl font-bold">{board.name}</h1>
              <Pencil className="w-4 h-4 text-muted-foreground" />
            </button>
          </div>

          <div className="flex items-center gap-3">
            <Button variant="outline" onClick={handleSave} data-scan-item className="gap-2">
              <Save className="w-4 h-4" />
              {t("save")}
            </Button>
            <Button onClick={handleSave} data-scan-item className="gap-2 bg-primary hover:bg-primary/90">
              <Check className="w-4 h-4" />
              {t("finish")}
            </Button>
          </div>
        </header>

        {/* Grid Settings */}
        <div className="px-6 py-4 border-b border-border bg-card/30">
          <div className="flex items-center justify-start gap-6 flex-wrap">
            <div className="flex items-center gap-2">
              <Grid3X3 className="w-5 h-5 text-muted-foreground" />
              <span className="text-lg font-medium text-muted-foreground">{t("gridSize")}</span>
            </div>

            <div className="flex items-center gap-3">
              <span className="text-sm text-muted-foreground">{t("rows")}:</span>
              <div className="flex items-center gap-1">
                <Button
                  variant="outline"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => board.rows > 1 && updateGridSize(board.rows - 1, board.columns)}
                  disabled={board.rows <= 1}
                >
                  <Minus className="w-4 h-4" />
                </Button>
                <span className="w-8 text-center font-bold">{board.rows}</span>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => board.rows < 8 && updateGridSize(board.rows + 1, board.columns)}
                  disabled={board.rows >= 8}
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <span className="text-sm text-muted-foreground">{t("columns")}:</span>
              <div className="flex items-center gap-1">
                <Button
                  variant="outline"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => board.columns > 2 && updateGridSize(board.rows, board.columns - 1)}
                  disabled={board.columns <= 2}
                >
                  <Minus className="w-4 h-4" />
                </Button>
                <span className="w-8 text-center font-bold">{board.columns}</span>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => board.columns < 8 && updateGridSize(board.rows, board.columns + 1)}
                  disabled={board.columns >= 8}
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            </div>

            <span className="text-sm text-muted-foreground">
              ({board.rows}x{board.columns} = {board.rows * board.columns} {t("cells")})
            </span>
          </div>
        </div>

        {/* Grid Builder */}
        <div className="flex-1 overflow-auto p-6 flex items-center justify-center">
          <div
            className="grid gap-4"
            style={{
              gridTemplateColumns: `repeat(${board.columns}, minmax(100px, 160px))`,
              gridTemplateRows: `repeat(${board.rows}, minmax(90px, 140px))`,
            }}
          >
            {board.cells.map((row, rowIndex) =>
              row.map((cell, colIndex) => {
                const colorClasses = cell ? getColorClasses(cell.color) : null;
                return (
                  <motion.button
                    key={`${rowIndex}-${colIndex}`}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: (rowIndex * board.columns + colIndex) * 0.02 }}
                    onClick={() => handleCellClick(rowIndex, colIndex)}
                    data-scan-item
                    className={cn(
                      "flex flex-col items-center justify-center gap-2 p-3",
                      "rounded-2xl border-2 transition-all duration-200",
                      "hover:scale-105 active:scale-95",
                      cell
                        ? cn(colorClasses?.bg, colorClasses?.border, "hover:ring-2 hover:ring-primary/50")
                        : "border-dashed border-border/60 bg-card/50 hover:border-primary hover:bg-primary/5"
                    )}
                  >
                    {renderCellPreview(cell)}
                  </motion.button>
                );
              })
            )}
          </div>
        </div>

      </div>
    </MainLayout>
  );
}
